"use client";

import { Link, usePathname } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import {
  LayoutDashboard,
  Radio,
  Settings,
  CreditCard,
  BookOpen,
  X,
  Plug2,
  Shield,
  LifeBuoy,
  Monitor,
  Workflow,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/stores/auth-store";
import { useSidebarStore } from "@/lib/stores/sidebar-store";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LogoTile } from "@/components/brand/logo-mark";

export function Sidebar() {
  const t = useTranslations();
  const pathname = usePathname();
  const { user } = useAuthStore();
  const { isOpen, close } = useSidebarStore();

  const navSections = [
    {
      label: t("dashboard.sidebar.monitor"),
      items: [
        { href: "/overview", label: t("dashboard.sidebar.home"), icon: LayoutDashboard },
        { href: "/signals", label: t("dashboard.sidebar.signals"), icon: Radio },
        // Analytics is hidden until the page has real content: /analytics
        // currently renders only a "coming soon" empty state. Restore this
        // entry (and its BarChart3 import) when the page ships.
      ],
    },
    {
      label: t("dashboard.sidebar.setup"),
      items: [
        { href: "/flows", label: t("dashboard.sidebar.flows"), icon: Workflow },
        { href: "/sources", label: t("dashboard.sidebar.sources"), icon: Plug2 },
        { href: "/destinations", label: t("dashboard.sidebar.destinations"), icon: Monitor },
      ],
    },
    {
      label: t("dashboard.sidebar.account"),
      items: [
        { href: "/settings/billing", label: t("dashboard.sidebar.billingUsage"), icon: CreditCard },
        { href: "/settings", label: t("dashboard.sidebar.settings"), icon: Settings },
        { href: "/support", label: t("dashboard.sidebar.support"), icon: LifeBuoy },
      ],
    },
  ];

  // Longest-prefix match so /settings/billing activates "Billing & usage"
  // (not "Settings") and /flows/abc keeps "Flows" active.
  const allHrefs = navSections.flatMap((s) => s.items.map((i) => i.href));
  const activeHref = allHrefs
    .filter((href) => pathname === href || pathname.startsWith(href + "/"))
    .sort((a, b) => b.length - a.length)[0];

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "U";

  const planLabel = user
    ? user.plan.charAt(0).toUpperCase() + user.plan.slice(1)
    : "";

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Wordmark: lowercase brand with gradient on the final "ync" */}
      <div className="flex h-16 items-center justify-between border-b border-border px-5">
        <Link
          href="/overview"
          className="flex items-center gap-2.5 font-heading text-lg font-extrabold lowercase tracking-tight"
        >
          <LogoTile className="size-7" />
          <span>
            {t("common.app.brandPrefix")}
            <span className="bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent">
              {t("common.app.brandSuffix")}
            </span>
          </span>
        </Link>
        <Button
          variant="ghost"
          size="icon-sm"
          className="lg:hidden"
          onClick={close}
          aria-label={t("dashboard.sidebar.closeMenu")}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label={t("dashboard.sidebar.mainNav")}>
        {navSections.map((section, sectionIdx) => (
          <div key={section.label} className={cn(sectionIdx > 0 && "mt-5")}>
            <p className="mb-1.5 px-3 text-[10.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
              {section.label}
            </p>
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const isActive = item.href === activeHref;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => close()}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      "flex items-center gap-2.5 rounded-lg border-s-2 border-s-transparent px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "border-s-primary bg-gradient-to-r from-primary/15 to-secondary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                    )}
                  >
                    <item.icon className="h-4 w-4" aria-hidden="true" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="space-y-3 border-t border-border p-4">
        {user && user.plan !== "advanced" && (
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
            <p className="text-xs font-semibold">{t("dashboard.sidebar.upgrade")}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t("dashboard.sidebar.upgradeDescription")}
            </p>
            <Button size="sm" className="mt-3 w-full" asChild>
              <Link href="/settings/billing">{t("dashboard.sidebar.upgradeButton")}</Link>
            </Button>
          </div>
        )}

        {user?.isAdmin && (
          <Link
            href="/admin/overview"
            className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
          >
            <Shield className="h-4 w-4" aria-hidden="true" />
            {t("dashboard.sidebar.adminPanel")}
          </Link>
        )}

        <Link
          href="/docs"
          className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
        >
          <BookOpen className="h-4 w-4" aria-hidden="true" />
          {t("dashboard.sidebar.documentation")}
        </Link>

        {user && (
          <div className="flex items-center gap-3 px-3 py-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-gradient-to-br from-primary/30 to-secondary/30 text-xs font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{user.name || t("dashboard.sidebar.userFallback")}</p>
              <p className="truncate text-xs text-muted-foreground">
                {user.isTrialActive
                  ? t("dashboard.sidebar.planTrial", { plan: planLabel })
                  : planLabel}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar (glass) */}
      <aside className="fixed start-0 top-0 z-40 hidden h-screen w-64 border-e border-border bg-background/60 backdrop-blur-xl lg:block">
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={close}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={cn(
          "fixed start-0 top-0 z-50 h-screen w-64 border-e border-border bg-background/90 backdrop-blur-xl transition-transform duration-300 lg:hidden",
          isOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"
        )}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
